import Game from '../../Game';
import PIXIRenderer from '../../System/Render/PIXIRenderer';
import { IScoreEntry } from './ISDKDriver';
import LeaderboardHelper from '../../System/UI/LeaderboardHelper';
import GameObject from '../../GameObject/GameObject';
import { isIOS } from 'mobile-device-detect';

export default class PayloadHelper {
    public static async getSendGiftBase64() {
        const key = 'friend_send_gift';

        await PayloadHelper.loadMessageTexture(
            key,
            'resource/graphics/messages/Bot_Notification_Gift_Coins.jpg'
        );
        return PIXIRenderer.textureToBase64(key);
    }

    public static async getSendLifeBase64() {
        const key = 'friend_send_life';

        await PayloadHelper.loadMessageTexture(
            key,
            'resource/graphics/messages/Bot_Notification_Friend_Send_Life.png'
        );

        const imageSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));

        const title = new PIXI.Text(
            Game.locale.getText('BotSendLifeTitle'),
            Game.style.getStyle('botSendLife.title')
        );
        title.anchor.set(0.5, 0.5);
        title.position.set(334, 30);
        imageSprite.addChild(title);

        return PIXIRenderer.displayObjectToBase64(imageSprite);
    }

    public static async getRequestLifeBase64() {
        const key = 'friend_request_life';

        await PayloadHelper.loadMessageTexture(
            key,
            'resource/graphics/messages/Bot_Notification_Friend_Request_Life.png'
        );

        const imageSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));

        const avatarSprite = new PIXI.Sprite(
            PIXIRenderer.getTexture(Game.player.getAvatarTexture())
        );
        avatarSprite.anchor.set(0.5, 0.5);
        avatarSprite.position.set(464, 180);
        avatarSprite.width = 150;
        avatarSprite.height = 150;

        const title = new PIXI.Text(
            Game.locale.getText('BotRequestLifeTitle'),
            Game.style.getStyle('botRequestLife.title')
        );
        title.anchor.set(0.5, 0.5);
        title.position.set(334, 30);

        const container = new PIXI.Sprite();
        container.addChild(avatarSprite);
        container.addChild(imageSprite);
        container.addChild(title);
        return PIXIRenderer.displayObjectToBase64(container);
    }

    public static async getInviteFriendBase64() {
        const key = 'share_invite_letsplay';

        await PayloadHelper.loadMessageTexture(
            key,
            'resource/graphics/messages/Bot_Notification_Invite.png'
        );

        const imageSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));

        const avatarSprite = new PIXI.Sprite(
            PIXIRenderer.getTexture(Game.player.getAvatarTexture())
        );
        avatarSprite.width = 150;
        avatarSprite.height = 150;
        avatarSprite.anchor.set(0.5, 0.5);
        avatarSprite.position.set(334, 180);

        const title = new PIXI.Text(
            Game.locale.getText('BotInviteFriendTitle'),
            Game.style.getStyle('botInviteFriend.title')
        );
        title.anchor.set(0.5, 0);
        title.position.set(334, 8);

        const container = new PIXI.Sprite();
        container.addChild(avatarSprite);
        container.addChild(imageSprite);
        container.addChild(title);
        return PIXIRenderer.displayObjectToBase64(container);
    }

    public static async getHelpFriendBase64() {
        const key = 'get_help_from_friend';

        if (isIOS) {
            await PayloadHelper.loadMessageTexture(
                key,
                'resource/graphics/messages/Bot_Notification_Friend_Help_Alt.png'
            );
        } else {
            await PayloadHelper.loadMessageTexture(
                key,
                'resource/graphics/messages/Bot_Notification_Friend_Help.png'
            );
        }

        const imageSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));
        const title = new PIXI.Text(
            Game.locale.getText('ThanksForHelpingMe'),
            Game.style.getStyle('botInviteFriend.title')
        );
        title.anchor.set(0.5, 0);
        title.position.set(334, 8);
        const container = new PIXI.Sprite();
        container.addChild(imageSprite);
        container.addChild(title);
        return PIXIRenderer.displayObjectToBase64(container);
    }

    public static async getGateLockedBase64() {
        const key = 'gate_unlock_message_locked';

        await PayloadHelper.loadMessageTexture(
            key,
            'resource/graphics/messages/Bot_Notification_Gate_Locked.jpg'
        );
        return PIXIRenderer.textureToBase64(key);
    }

    public static async getGateUnlockedBase64() {
        const key = 'gate_unlock_message_open';

        await PayloadHelper.loadMessageTexture(
            key,
            'resource/graphics/messages/Bot_Notification_Gate_Open.jpg'
        );
        return PIXIRenderer.textureToBase64(key);
    }

    public static getScreenshotBase64() {
        const renderTexture = PIXI.RenderTexture.create(
            PIXIRenderer.renderer.width,
            PIXIRenderer.renderer.height
        );
        PIXIRenderer.renderer.render(
            PIXIRenderer.sceneContainer,
            renderTexture
        );
        return PIXIRenderer.renderer.extract.base64(renderTexture);
    }

    public static async getBeatHighscoreLevelBase64(
        levelNumber: number,
        playerEntry: IScoreEntry,
        friendEntry: IScoreEntry
    ) {
        const key = 'beat_highscore_level';

        await PayloadHelper.loadMessageTexture(
            key,
            'resource/graphics/messages/Bot_Message_Beat_Highscore_Level.png'
        );

        const imageSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));

        // Setup player (winner) info.
        const winnerAvatarId = LeaderboardHelper.getPlayerAvatarId(
            playerEntry.user.playerId,
            playerEntry.user.isCurrentUser
        );
        const winnerAvatar = new PIXI.Sprite(
            PIXIRenderer.getTexture(
                Game.resource.exists(winnerAvatarId)
                    ? winnerAvatarId
                    : 'no_avatar'
            )
        );
        winnerAvatar.width = 100;
        winnerAvatar.height = 100;
        winnerAvatar.anchor.set(0.5, 0.5);
        winnerAvatar.position.set(120, 112);
        imageSprite.addChild(winnerAvatar);

        const winnerNameText = new PIXI.Text(
            playerEntry.user.handle,
            Game.style.getStyle('botBeatHighscore.winnerName')
        );
        winnerNameText.anchor.set(0, 0.5);
        winnerNameText.position.set(206, 86);
        imageSprite.addChild(winnerNameText);

        const winnerScoreText = new PIXI.Text(
            playerEntry.score.toString(),
            Game.style.getStyle('botBeatHighscore.winnerScore')
        );
        winnerScoreText.anchor.set(0, 0.5);
        winnerScoreText.position.set(206, 138);
        imageSprite.addChild(winnerScoreText);

        // Setup friend (loser) info.
        const loserAvatarId = LeaderboardHelper.getPlayerAvatarId(
            friendEntry.user.playerId,
            friendEntry.user.isCurrentUser
        );
        const loserAvatar = new PIXI.Sprite(
            PIXIRenderer.getTexture(
                Game.resource.exists(loserAvatarId)
                    ? loserAvatarId
                    : 'no_avatar'
            )
        );
        loserAvatar.width = 80;
        loserAvatar.height = 80;
        loserAvatar.anchor.set(0.5, 0.5);
        loserAvatar.position.set(132, 246);
        imageSprite.addChild(loserAvatar);

        const loserNameText = new PIXI.Text(
            friendEntry.user.handle,
            Game.style.getStyle('botBeatHighscore.loserName')
        );
        loserNameText.anchor.set(0, 0.5);
        loserNameText.position.set(206, 224);
        imageSprite.addChild(loserNameText);

        const loserScore = new PIXI.Text(
            friendEntry.score.toString(),
            Game.style.getStyle('botBeatHighscore.loserScore')
        );
        loserScore.anchor.set(0, 0.5);
        loserScore.position.set(206, 268);
        imageSprite.addChild(loserScore);

        const levelText = new PIXI.Text(
            Game.locale
                .getText('Level')
                .replace('[x]', '')
                .trim(),
            Game.style.getStyle('botBeatHighscore.level')
        );
        levelText.anchor.set(0.5, 0.5);
        levelText.position.set(585, 102);
        imageSprite.addChild(levelText);

        const levelNumberText = new PIXI.Text(
            levelNumber.toString(),
            Game.style.getStyle('botBeatHighscore.levelNumber')
        );
        levelNumberText.anchor.set(0.5, 0.5);
        levelNumberText.position.set(585, 172);
        imageSprite.addChild(levelNumberText);

        return PIXIRenderer.displayObjectToBase64(imageSprite);
    }

    public static async get3StarRewardBase64(levelNumber: number) {
        const key = 'share_three_star_reward';

        await PayloadHelper.loadMessageTexture(
            key,
            'resource/graphics/messages/Share_Three_Stars.jpg'
        );

        const imageSprite = new PIXI.Sprite(PIXIRenderer.getTexture(key));

        const levelNumberText = new PIXI.Text(
            levelNumber.toString(),
            Game.style.getStyle('bot3StarReward.levelNumber')
        );
        levelNumberText.anchor.set(0.5, 0.5);
        levelNumberText.position.set(408, 142);
        imageSprite.addChild(levelNumberText);

        return PIXIRenderer.displayObjectToBase64(imageSprite);
    }

    public static getGameObjectBase64(id: string): string {
        const gameObject = GameObject.findByID(id);
        if (!gameObject || gameObject.destroyed) return;
        return PIXIRenderer.displayObjectToBase64(gameObject.container);
    }

    private static async loadMessageTexture(
        key: string,
        path: string
    ): Promise<void> {
        Game.resource.add(key, path);
        return await Game.resource.wait(key);
    }
}
